import React, { useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";

import { AvatarSize } from "@performyard/py-components";

import AvatarGroupModal from "./AvatarGroupModal";

//utils
import { getAvatarEntry } from "./utils";

// compnents
import Typography from "../Text";
import Tooltip from "../Tooltip";
// css
import css from "./AvatarGroup.module.scss";

const CAP = "cap";

export type AvatarGroupProps = {
	/**
	 * Array of employee IDs to display as Avatar Chips
	 */
	employees: { _id: string; name: string }[];
	/**
	 * Size of the Avatar Chips, directly becomes the size prop of the Avatar component
	 */
	size?: AvatarSize;
	/**
	 * Maximum number of Avatar Chips to display. If the number of employeeIDs is greater than the maxLength, a "cap" element will be added to the end of the list
	 */
	maxLength: number;
	/**
	 * Whether or not the Avatar Chips should be clickable and clicking should link to the employee's dashboard. This also give the Avatars the employee hover state.
	 */
	hasLinks?: boolean;

	/**
	 * Whether or not the Avatar Chips should be clickable and clicking should open a modal with all avatars in the group if its truncated
	 */
	canShowModal?: boolean;
	/**
	 * Just show employee name tooltip instead of employee card on hover
	 */
	employeeNameTooltip?: boolean;
};

const AvatarGroup = ({
	employees = [],
	size = 20,
	maxLength,
	hasLinks = false,
	canShowModal = false,
	employeeNameTooltip = false,
}: AvatarGroupProps) => {
	const groupRef = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver<HTMLDivElement>({
		ref: groupRef,
	});
	const [modalOpen, setModalOpen] = useState(false);
	const employeeIDs = employees.map((employee) => employee._id);

	// If the cappedEmployees array is longer than the maxLength, we need to cap it and add a "cap" element
	const cappedEmployees = maxLength ? employees.slice(0, maxLength) : employees;

	const isEmployeesCapped = cappedEmployees.length < employeeIDs.length;

	if (isEmployeesCapped) {
		cappedEmployees.push({ _id: "cap", name: "CAP" });
	}

	// If the width is 0, we can't calculate how many avatars to show per row
	// calculate how many avatars to show per row can be shown given the width of the container
	const avatarRows: { _id: string; name: string }[][] = [];
	let avatarsInRow = 0;
	const adjustedSize = size + 2;

	const renderWidth = width ? width : adjustedSize * maxLength + 1;
	avatarsInRow = Math.floor(renderWidth / (adjustedSize - adjustedSize / 4));
	let rowIndex = 0;
	cappedEmployees.forEach((employee: { _id: string; name: string }) => {
		if (!avatarRows[rowIndex]) {
			avatarRows[rowIndex] = [];
		}
		avatarRows[rowIndex].push(employee);
		if (avatarRows[rowIndex].length >= avatarsInRow) {
			rowIndex++;
		}
	});

	const canExpand = isEmployeesCapped && canShowModal;

	return (
		<div data-testid="avatar-group">
			<Tooltip message="View All" strategy="fixed" disabled={!canExpand}>
				<div
					ref={groupRef}
					className={`${css.avatarGroup} ${canExpand ? css.expandable : null}`}
					onClick={() => canExpand && setModalOpen(true)}
				>
					{avatarRows.map((employeeRows: { _id: string; name: string }[]) => {
						return (
							<div className={css.row} key={`avatarRow-${employeeRows[0]._id}`}>
								{employeeRows?.map((employee: { _id: string; name: string }, index: number) => {
									const style = index > 0 ? { marginLeft: -adjustedSize / 4 } : {};

									if (employee._id === CAP) {
										return (
											<div
												style={style}
												className={css.avatarContainer}
												key={`avatarChip-${employee._id}`}
											>
												<div style={{ minWidth: size, height: size }} className={css.capChip}>
													<Typography color="neutral-0" weight="regular" size="caption">
														+{employeeIDs.length - (cappedEmployees.length - 1)}
													</Typography>
												</div>
											</div>
										);
									}

									return getAvatarEntry(
										hasLinks,
										canExpand,
										employeeNameTooltip,
										employee,
										style,
										size,
										employeeIDs.length > 1
									);
								})}
							</div>
						);
					})}
				</div>
			</Tooltip>
			<AvatarGroupModal isOpen={modalOpen} employees={employees} onClose={() => setModalOpen(false)} />
		</div>
	);
};

export default AvatarGroup;
